import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CourseChrome from "../components/CourseChrome.js";
import { api, type CourseDetail } from "../api.js";

/**
 * One course, as the admin sees it: staffing, batches, what has been set, and
 * what is waiting on a teacher.
 *
 * Read-only on purpose. An admin has the rights to edit any course in their
 * institution, but silently rewriting a colleague's assignment is not oversight
 * -- so the actions here lead to the teacher's own pages rather than duplicating
 * them.
 */
export default function InstitutionCourse() {
  const { id } = useParams();
  const [data, setData] = useState<CourseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<CourseDetail>(`/api/institution/courses/${id}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [id]);

  if (error) {
    return (
      <div className="page">
        <div className="error-banner">{error}</div>
        <Link to="/"><button>Back to institution</button></Link>
      </div>
    );
  }
  if (!data) return <div className="page muted">Loading…</div>;

  const { course, teachers, assignments, exams } = data;
  const now = Date.now();

  return (
    <div className="page">
      <CourseChrome
        section="assignments"
        title="Course"
        course={course}
        actions={<Link to="/"><button>All courses</button></Link>}
      />
      <div className="muted" style={{ margin: "-6px 0 14px", fontSize: 13 }}>
        Created by {course.owner.name}
      </div>

      <div className="card">
        <div className="stats">
          <div className="stat">
            <div className="value">{data.studentCount}</div>
            <div className="label">students</div>
          </div>
          <div className="stat">
            <div className="value">{assignments.length}</div>
            <div className="label">assignments</div>
          </div>
          <div className="stat">
            <div className="value">{exams.length}</div>
            <div className="label">exams</div>
          </div>
          <div className="stat">
            <div className="value" style={data.unbatchedCount ? { color: "var(--warn)" } : undefined}>
              {data.unbatchedCount}
            </div>
            <div className="label">unbatched</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>TEACHING</div>
        {teachers.length === 0 ? (
          <div className="notice" style={{ marginBottom: 0 }}>
            Nobody teaches this course. Add a teacher to its roster before the term starts.
          </div>
        ) : (
          <div className="row" style={{ flexWrap: "wrap" }}>
            {teachers.map((t) => (
              <span key={t.id} className="badge draft">
                {t.name} · {t.email}
                {!t.active && " (inactive)"}
              </span>
            ))}
          </div>
        )}
      </div>

      {course.batches.length > 0 && (
        <div className="card">
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>LAB BATCHES</div>
          <table>
            <thead>
              <tr><th>Batch</th><th>Schedule</th><th>Students</th></tr>
            </thead>
            <tbody>
              {course.batches.map((b) => (
                <tr key={b.id}>
                  <td><strong>{b.name}</strong></td>
                  <td className="muted">{b.scheduleNote || "—"}</td>
                  <td className="muted">{b.studentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>ASSIGNMENTS</div>
        {assignments.length === 0 ? (
          <div className="muted">Nothing set yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th><th>Due</th><th>Tests</th><th>Submissions</th>
                <th>Awaiting review</th><th />
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.title}{" "}
                    {!a.published && <span className="badge draft">draft</span>}{" "}
                    {a.visibility === "EXAM_ONLY" && <span className="badge draft">exam only</span>}
                  </td>
                  <td className="muted">
                    {a.dueAt ? new Date(a.dueAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="muted">{a.testCaseCount}</td>
                  <td className="muted">{a.submissionCount}</td>
                  <td>
                    {a.pendingCount > 0 ? (
                      <span className="badge warn">{a.pendingCount}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/assignments/${a.id}/submissions`}><button>Submissions</button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>EXAMS</div>
        {exams.length === 0 ? (
          <div className="muted">No exams.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Title</th><th>Window</th><th>Questions</th><th>Attempts</th><th /></tr>
            </thead>
            <tbody>
              {exams.map((e) => {
                const open =
                  now >= new Date(e.opensAt).getTime() && now <= new Date(e.closesAt).getTime();
                return (
                  <tr key={e.id}>
                    <td>
                      {e.title}{" "}
                      {!e.published && <span className="badge draft">draft</span>}{" "}
                      {e.published && open && <span className="badge warn">open now</span>}
                    </td>
                    <td className="muted">
                      {new Date(e.opensAt).toLocaleString()} — {new Date(e.closesAt).toLocaleString()}
                      {e.durationMinutes ? ` · ${e.durationMinutes} min` : ""}
                    </td>
                    <td className="muted">{e.questionCount}</td>
                    <td className="muted">{e.attemptCount}</td>
                    <td>
                      <Link to={`/exams/${e.id}/edit`}><button>Open</button></Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
